import { router } from "@inertiajs/react"
import {
  ArrowLeft,
  BadgeCheck,
  Ban,
  Send,
  Undo2,
  UserMinus,
  X,
} from "lucide-react"
import type { ComponentType, SVGProps } from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  RejectionReasonPicker,
  isNoteMissing,
  isNoteRequired,
} from "@/components/candidates/rejection-reason-picker"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useIsMobile } from "@/hooks/use-mobile"
import { useOnline } from "@/hooks/use-online"
import { useRejectionReasonOptionsFor } from "@/hooks/use-rejection-reason-options"
import { cn } from "@/lib/utils"
import type { CandidateActions } from "@/types/candidates"

/**
 * F10 — Reject reason soft + hard limits. Mirrors F5 BulkRejectConfirmation
 * + F9 BlocklistConfirmation patterns so the char-counter UX is consistent
 * across every reason-bearing modal.
 */
const MAX_REJECT_REASON_LENGTH = 1000
const SOFT_REJECT_REASON_LIMIT = 300
const REJECT_REASON_ARIA_THRESHOLD = 240

/**
 * OKS-19 — Detail-page action bar with 8-event contextual rendering.
 *
 * **Refactor of OKS-18's Approve/Reject pair into the full transition
 * matrix.** Single visibility rule (FE TRD §State + UX Spec §Detail
 * action buttons): `actions.<verb>_url === null → button DOM-absent`.
 * No disabled-with-tooltip path. Both state-illegal AND role-locked
 * transitions are hidden via the same rule (BE TRD §Key Decisions —
 * URL nullability is the single discriminator).
 *
 * **OKS-18 dead-code removed:**
 *   - The `approval_workflow_pending_tooltip` i18n key (orphan after
 *     this commit; F12 grep-sweep removes it from locale files).
 *   - `Tooltip` / `TooltipContent` / `TooltipProvider` / `TooltipTrigger`
 *     imports — no disabled-tooltip path remains.
 *   - The `pendingTooltip` ActionButton helper variant.
 *
 * **Reading order (UX Spec §Per-State Action Bar — joint freeze):**
 * destructive (leftmost) · corrective (middle) · forward-primary
 * (rightmost). Encoded as the iteration order of `BUTTON_DEFS` below.
 * Per state at most 3 buttons render — fits inline at every breakpoint
 * (no overflow menu, CTPO-locked).
 *
 * **OKS-673 — green-flow primary actions relocated inline.** The two
 * forward-primary greens `Mark as reviewed` (`new`) and `Approve`
 * (`reviewed`) were REMOVED from this bar and now render inline in the
 * Invitations section (`invites-panel-empty.tsx`), driven by the SAME
 * `actions.mark_reviewed_url` / `actions.approve_url` nullability +
 * the SAME `{transition: {event}}` wire — no gate change. Their testids
 * (`candidate-detail-mark-reviewed` / `candidate-detail-approve`) moved
 * verbatim with them (location-agnostic selectors keep working).
 *
 * **OKS-674 — redundant `Invite` CTA removed.** The action-bar `Invite`
 * button (`approved`) was DELETED. Generating an invite link in the
 * Invitations section (`invites-panel-empty.tsx`) is now the single
 * affordance that drives `approved → invited` (via
 * `CandidateInvites::GenerateService`). Policy parity holds — every role
 * that saw the button (`CandidatePolicy#transition?(event: "invite")`)
 * can generate a link (`CandidateInvitePolicy#create?`), same
 * `write_permitted_role?` gate — so no role loses the ability to invite.
 *
 * **Per-state matrix** (driven entirely by BE-supplied URL nullability):
 *   - `new` → (green Mark-as-reviewed now inline in the section). Admin: Add to blocklist.
 *   - `reviewed` → Reject (outline) + Send for validation (outline). (green
 *     Approve now inline in the section). Admin: + Add to blocklist.
 *   - `pending_brand_validation` → Validate (default — no competing green
 *     here) + Reject (outline). Brand members only; an agency member gets a
 *     null `validate_url` and the button is DOM-absent.
 *   - `contacted` → Move back (outline) + Reject (outline). Inviting happens
 *     via the Invitations section, which treats `contacted` exactly like
 *     `approved` (PRD AC9).
 *   - `approved` → Move back (outline). Admin: + Add to blocklist.
 *     OKS-674: no forward-primary — inviting happens via the Invitations
 *     section's Generate-link, not the action bar.
 *     OKS-951: `Mark as contacted` was REMOVED from this bar — it flipped the
 *     status with no contact-attempt row behind it. It now lives in the contact
 *     panel, which records what was sent and on which channel in the same
 *     transaction. See the note at the end of `BUTTON_DEFS`.
 *   - `invited` → Remove from pipeline (destructive, brand-admin tier). Admin: + Add to blocklist.
 *     OKS-566: no forward-primary — the candidate self-joins via the invite link
 *     (`CandidateInvites::CompleteService`), never a brand click.
 *   - `joined` → empty for brand/agency. Admin: Add to blocklist only — **QA regression target**.
 *   - `rejected` → Move back (outline, lands where they were rejected from).
 *     Admin: + Add to blocklist.
 *   - `blocklisted` → Lift blocklist (destructive, admin only).
 *
 * **Wire format (BE TRD lock — nested strong params):** every
 * direct-POST routes through `router.post(actions.*_url, {transition:
 * {event: "..."}}, ...)`. Confirmation-modal buttons
 * (Reject, Revert, Blocklist, Unblocklist) open their respective
 * modals — Reject's modal lives inline (extended in F10 with the
 * optional reason textarea); the other 3 are owned by the parent
 * (`pages/Brand/Candidates/Show.tsx`) via callback props (F8/F9 wire).
 *
 * **Locked testids (FE TRD §testid Map — frozen):**
 *   - OKS-18-frozen: `candidate-detail-reject` ·
 *     `candidate-detail-reject-confirm` (preserved verbatim)
 *   - OKS-674-removed: `candidate-detail-invite` (was F7 additive)
 *   - OKS-673-relocated (now in the Invitations section, testids unchanged):
 *     `candidate-detail-mark-reviewed` · `candidate-detail-approve`
 *   - F8/F9 callback-driven (rendered by F7, modal in F8/F9):
 *     `candidate-detail-revert` · `candidate-detail-blocklist` ·
 *     `candidate-detail-unblocklist`
 */
export interface CandidateActionBarProps {
  actions: CandidateActions
  /**
   * F8 — opens the RevertConfirmation modal. Parent (Show.tsx) owns
   * the modal state. F7 ships the button that triggers this callback;
   * F8 wires the actual modal mount.
   */
  onRevertClick?: () => void
  /**
   * F9 — opens the BlocklistConfirmation modal (REQUIRED reason).
   * Same parent-owns-modal pattern.
   */
  onBlocklistClick?: () => void
  /**
   * F9 — opens the UnblocklistConfirmation modal (no reason field).
   */
  onUnblocklistClick?: () => void
  /**
   * OKS-465 — Remove-from-pipeline affordance URL (from
   * `removal_actions.remove_url`, NOT the transition `actions` surface).
   * Non-null → the leftmost destructive "Remove from pipeline" button
   * renders with an AlertDialog confirm. Null/undefined → DOM-absent.
   */
  removeUrl?: string | null
  /**
   * OKS-465 — candidate TikTok handle, used in the Remove confirmation
   * AlertDialog copy + accessible name ("Remove @handle from pipeline").
   */
  handle?: string
  /**
   * OKS-952 — the candidate's CURRENT status, i.e. the `from_status` a
   * rejection would be recorded against. Selects this candidate's slice of the
   * server-sent reason taxonomy.
   *
   * Optional so the prop is additive; without it the picker is DOM-absent and
   * the modal behaves exactly as it did before this ticket (free-text note
   * only), which is the correct degradation rather than a broken dropdown.
   *
   * ⚠️ This is the ONLY surface that reaches all five rejectable statuses —
   * every card serializer emits `reject_url` for `reviewed` or
   * `pending_brand_validation` only. Gate-separation is therefore verifiable
   * here and vacuous on the cards.
   */
  status?: string | null
  /**
   * OKS-998 — reject-modal open state, CONTROLLED when supplied and
   * uncontrolled otherwise (`rejectOpen ?? internalRejectOpen`).
   *
   * `Show.tsx` supplies it so the sticky decision bar's Reject can open
   * THIS component's modal instead of mounting a second one. That keeps
   * exactly one `RejectionReasonPicker`, one `submitReject` and one
   * `candidate-detail-reject-confirm` on the page, which several
   * page-scoped Playwright selectors depend on.
   *
   * ⚠️ Ownership of the reject FLOW deliberately stays here rather than
   * being lifted to the page: extracting it would relocate
   * `candidate-detail-reject-confirm` into a new file that page-scoped
   * specs reach, for no user-visible gain.
   */
  rejectOpen?: boolean
  /**
   * OKS-998 — companion to `rejectOpen`. Fires on every open/close of the
   * reject modal, including ESC and overlay-click.
   *
   * 🔴 This component's own Reject button is the SOLE caller with `true`.
   * `Show.tsx` relies on that to record which trigger opened the modal
   * (header vs sticky) without any origin argument being plumbed through.
   * If a second internal call site is ever added, the focus-return target
   * in `Show.tsx` becomes wrong and nothing will fail loudly.
   */
  onRejectOpenChange?: (open: boolean) => void
  /**
   * OKS-998 — close-focus target for the reject modal, as an
   * `app/frontend/lib/final-focus.ts` getter resolved at close time.
   *
   * Both modal branches are Base UI since OKS-1022 (≥768px `Dialog`, <768px
   * `Drawer`), so this one getter is passed as `finalFocus` to each. Base UI
   * reads a returned element as the target and `null` as "use the default"
   * (the still-connected trigger or previously focused element) — after a
   * successful reject both Reject buttons are unmounted, so focus is released,
   * never trapped. Before OKS-1022 the <768px branch was vaul (Radix Dialog)
   * and needed a separate `onCloseAutoFocus` handler; verify focus work at
   * BOTH viewport bands all the same — they are different components.
   *
   * Undefined → both branches keep their default behaviour, which is what
   * every pre-OKS-998 caller gets.
   */
  rejectFinalFocus?: () => HTMLElement | null
  className?: string
}

/**
 * Pure mapping — `*_url` key → testid + i18n label key + variant +
 * dispatcher kind. The reading-order convention (destructive ·
 * corrective · forward) is encoded as the array order: the render loop
 * iterates this list and emits a button for every entry whose URL is
 * non-null.
 */
type ActionUrlKey = keyof CandidateActions
type DispatcherKind =
  | "post"
  | "modal-reject"
  | "modal-revert"
  | "modal-blocklist"
  | "modal-unblocklist"
  /** OKS-950 — the optional note for the brand, owned inline like reject. */
  | "modal-request-validation"

interface ButtonDef {
  urlKey: ActionUrlKey
  testId: string
  labelKey: string
  variant: "default" | "outline" | "destructive"
  /** BE-locked event string posted in `{transition: {event}}`. */
  event: string
  /**
   * Lucide icon (component reference, NOT element) — rendered with
   * `aria-hidden` so the label carries semantic meaning.
   */
  icon: ComponentType<SVGProps<SVGSVGElement>>
  /** "post" → direct programmatic Inertia POST. "modal-*" → opens a confirmation modal. */
  kind: DispatcherKind
}

const BUTTON_DEFS: readonly ButtonDef[] = [
  // ── Destructive — admin-only ────────────────────────────────────
  {
    urlKey: "blocklist_url",
    testId: "candidate-detail-blocklist",
    labelKey: "candidates.detail.actions.blocklist",
    variant: "destructive",
    event: "blocklist",
    icon: Ban,
    kind: "modal-blocklist",
  },
  {
    urlKey: "unblocklist_url",
    testId: "candidate-detail-unblocklist",
    labelKey: "candidates.detail.actions.unblocklist",
    variant: "destructive",
    event: "unblocklist",
    icon: Undo2,
    kind: "modal-unblocklist",
  },
  // ── Corrective (middle) — any seated user ───────────────────────
  {
    urlKey: "revert_url",
    testId: "candidate-detail-revert",
    labelKey: "candidates.detail.actions.revert",
    variant: "outline",
    event: "revert",
    icon: ArrowLeft,
    kind: "modal-revert",
  },
  {
    urlKey: "reject_url",
    testId: "candidate-detail-reject",
    labelKey: "candidates.detail.actions.reject",
    variant: "outline",
    event: "reject",
    icon: X,
    kind: "modal-reject",
  },
  // ── Forward (rightmost) — any seated user ───────────────────────
  // OKS-673 relocated `mark_reviewed` + `approve` inline into the
  // Invitations section (`invites-panel-empty.tsx`). OKS-674 removed
  // the redundant `invite` CTA — generating an invite link there is the
  // single affordance that drives `approved`/`contacted` → `invited`.
  //
  // OKS-948 adds three forward steps here, in pipeline order. Both new
  // pipeline steps are OPTIONAL: Reviewed → To contact directly still
  // works, and To contact → Invited still works.
  //
  // "One green per state" (UX Spec §2): the green forward-primary lives in
  // the Invitations section, so the two steps that COMPETE with it are
  // `outline`. `pending_brand_validation` has no competing green — nothing
  // renders in the Invitations section at that status — so Validate is the
  // primary there. The richer affordance logic (split button, primacy
  // driven by the brand's opt-in setting) is OKS-950's scope; do not
  // build it here.
  {
    urlKey: "request_brand_validation_url",
    testId: "candidate-detail-request-brand-validation",
    labelKey: "candidates.detail.actions.request_brand_validation",
    variant: "outline",
    event: "request_brand_validation",
    icon: Send,
    // OKS-950 — was a direct POST; now opens the note dialog. The note is
    // OPTIONAL (PRD OQ5) and confirming with an empty textarea posts exactly
    // what the direct POST used to, so this costs the reviewer one click and
    // buys the brand the only context it will ever get about WHY this creator
    // was picked. That note is the whole content of the brand's queue card.
    kind: "modal-request-validation",
  },
  {
    urlKey: "validate_url",
    testId: "candidate-detail-validate",
    labelKey: "candidates.detail.actions.validate",
    variant: "default",
    event: "validate",
    icon: BadgeCheck,
    kind: "post",
  },
  /*
   * OKS-951 — the bare "Mark as contacted" action was REMOVED from this bar.
   *
   * It flipped `approved → contacted` through the generic transitions endpoint
   * with NO contact-attempt row behind it. After OKS-951 that produces a
   * candidate sitting in « À relancer » showing zero attempts, who is then
   * auto-rejected at +17 with `no_response` having never actually been
   * contacted — the unverifiable record CTPO decision D2 exists to prevent.
   *
   * The action now lives in the contact panel, which records what was sent, on
   * which channel, by whom, in the same transaction as the status change.
   *
   * ⚠️ This removal is the AFFORDANCE half only. The server refuses the event
   * on `#transitions` regardless of what this bar renders — see
   * `CandidatesController::ENDPOINT_EXCLUDED_EVENTS`. A stale tab defeats a
   * missing button; it does not defeat the endpoint.
   */
] as const

export function CandidateActionBar({
  actions,
  onRevertClick,
  onBlocklistClick,
  onUnblocklistClick,
  removeUrl = null,
  handle,
  status = null,
  rejectOpen,
  onRejectOpenChange,
  rejectFinalFocus,
  className,
}: CandidateActionBarProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const isMobile = useIsMobile()
  // OKS-998 — controlled-when-supplied. `internalRejectOpen` remains the
  // source of truth for every caller that does not pass `rejectOpen`, so
  // this component's behaviour is unchanged outside the detail page.
  const [internalRejectOpen, setInternalRejectOpen] = useState(false)
  const rejectModalOpen = rejectOpen ?? internalRejectOpen
  // OKS-465 — Remove-from-pipeline confirmation (shadcn AlertDialog).
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  // F10 — optional reason textarea state (UX Spec §Reject modal
  // extension). Reason is OPTIONAL per PRD; submit is enabled
  // regardless of length. Resets on modal close/success.
  const [rejectReason, setRejectReason] = useState("")
  // OKS-952 — coded reason + the client-side "other needs a note" flag. The
  // flag is only raised on a blocked submit, never on selection: you cannot be
  // wrong before you have had a chance to be right.
  const [rejectReasonCode, setRejectReasonCode] = useState("")
  const [rejectNoteMissing, setRejectNoteMissing] = useState(false)
  const rejectOptions = useRejectionReasonOptionsFor(status)
  const rejectReasonLength = rejectReason.length

  // OKS-950 — the optional note for the brand. Owned inline, like reject's,
  // rather than lifted to the parent: it is a plain textarea with no
  // cross-component state, and the two confirmation modals the parent DOES
  // own (revert, blocklist) are there because they carry server-supplied
  // props this bar never receives.
  const [requestValidationOpen, setRequestValidationOpen] = useState(false)
  const [validationNote, setValidationNote] = useState("")
  const rejectReasonOverSoft = rejectReasonLength >= SOFT_REJECT_REASON_LIMIT
  const rejectReasonShouldAnnounce =
    rejectReasonLength >= REJECT_REASON_ARIA_THRESHOLD

  /**
   * Render the buttons for which BE has supplied a non-null URL,
   * preserving BUTTON_DEFS order (destructive · corrective · forward).
   * If every URL is null (e.g. brand/agency on a `joined` candidate),
   * the bar renders empty — that's the QA-12c regression target.
   */
  const visibleButtons = BUTTON_DEFS.filter(
    (def) => actions[def.urlKey] !== null,
  )

  function dispatchPost(url: string, event: string) {
    if (submitting || !online) return
    setSubmitting(true)
    // BE TRD lock — nested strong-params wire format:
    //   params.require(:transition).permit(:event, :reason)
    // Posting the flat shape `{event}` would 400 (missing :transition
    // root). Vitest covers the call payload (memory
    // `feedback_vitest_wire_format_canary`).
    router.post(
      url,
      { transition: { event } },
      {
        preserveScroll: true,
        onFinish: () => setSubmitting(false),
      },
    )
  }

  function handleClick(def: ButtonDef) {
    const url = actions[def.urlKey]
    if (!url) return // defensive — visibleButtons filter already guards
    switch (def.kind) {
      case "post":
        return dispatchPost(url, def.event)
      case "modal-reject":
        // OKS-998 — through the shared handler, not `setInternalRejectOpen`
        // directly, so a controlling parent is told the HEADER opened it.
        // This is the sole internal call site with `true`.
        return handleRejectModalChange(true)
      case "modal-revert":
        return onRevertClick?.()
      case "modal-blocklist":
        return onBlocklistClick?.()
      case "modal-unblocklist":
        return onUnblocklistClick?.()
      case "modal-request-validation":
        return setRequestValidationOpen(true)
    }
  }

  /**
   * OKS-465 — Confirm soft-remove. Plain programmatic POST (no body —
   * remove takes no fields), mirroring the transition action-bar
   * pattern. The AlertDialog gates the POST; the Confirm button shows
   * the shared `submitting` busy state and respects the offline guard.
   */
  function submitRemove() {
    if (!removeUrl || submitting || !online) return
    setSubmitting(true)
    router.post(
      removeUrl,
      {},
      {
        preserveScroll: true,
        onSuccess: () => setRemoveDialogOpen(false),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  /**
   * OKS-950 — send for brand validation, with the optional note.
   *
   * Mirrors `submitReject`'s payload shape: the note is OPTIONAL (PRD OQ5),
   * so an empty textarea posts `{transition: {event}}` — byte-identical to
   * the direct POST this button used to make. Confirming without typing is
   * therefore not a degraded path; it is the old behaviour exactly.
   */
  function submitRequestValidation() {
    const url = actions.request_brand_validation_url
    if (!url || submitting || !online) return
    setSubmitting(true)
    const trimmed = validationNote.trim()
    const transition =
      trimmed.length > 0
        ? { event: "request_brand_validation", reason: validationNote }
        : { event: "request_brand_validation" }
    router.post(
      url,
      { transition },
      {
        preserveScroll: true,
        onSuccess: () => {
          setRequestValidationOpen(false)
          setValidationNote("")
        },
        onFinish: () => setSubmitting(false),
      },
    )
  }

  /** Clear the buffer on any close, so a later candidate can't inherit it. */
  function handleRequestValidationChange(open: boolean) {
    setRequestValidationOpen(open)
    if (!open) setValidationNote("")
  }

  function submitReject() {
    const url = actions.reject_url
    if (!url || submitting || !online) return
    // OKS-952 — latency-only guard. The server re-checks and is authoritative
    // (`:reason_required_for_other`), so a bypassed client still fails closed.
    //
    // 🔴 OKS-998 — THIS GATE MUST STAY ABOVE `setSubmitting(true)`, and the
    // ordering is the whole fix. Until this ticket `setSubmitting(true)` ran
    // first, and because this branch issues NO request there is no `onFinish`
    // to reset it — so `submitting` stayed true forever. The textarea, the
    // confirm and the cancel all carry `disabled={submitting || !online}`,
    // which locked the user out of fixing the exact thing they had just been
    // told to fix: the field rendered `disabled` and `aria-invalid="true"` at
    // the same time, and the only escape was closing and reopening the picker.
    //
    // A guard that issues no request must not claim the in-flight flag.
    // `candidate-review-actions.tsx`'s `submitReject` already had this order;
    // this one was the outlier, and `bulk-reject-confirmation.tsx` avoids the
    // class entirely by returning `false` from Inertia `<Form>`'s `onBefore`.
    if (isNoteMissing(rejectReasonCode, rejectReason)) {
      setRejectNoteMissing(true)
      return
    }
    setRejectNoteMissing(false)
    setSubmitting(true)
    // F10 — reason is OPTIONAL per PRD; pass it whenever the user
    // typed something. BE accepts both empty-string and absent
    // (`params.require(:transition).permit(:event, :reason)` ignores
    // missing keys). We pass undefined when empty so the wire payload
    // stays minimal — `{transition: {event: "reject"}}` for no-reason,
    // `{transition: {event: "reject", reason: "..."}}` with reason.

    const trimmed = rejectReason.trim()
    // Keys are omitted rather than sent empty, preserving the shipped minimal
    // payload: `{transition: {event: "reject"}}` for a bare rejection. The
    // picker's "no reason given" option is an empty string, which collapses
    // here and again in the service's `.strip.presence`.
    const transitionPayload = {
      event: "reject",
      ...(trimmed.length > 0 ? { reason: rejectReason } : {}),
      ...(rejectReasonCode ? { reason_code: rejectReasonCode } : {}),
    }
    router.post(
      url,
      { transition: transitionPayload },
      {
        preserveScroll: true,
        // OKS-998 — must close through the shared handler, not by setting
        // internal state: under a controlling parent (`Show.tsx`) a local-only
        // close would leave `rejectOpen` true and the modal would stay open
        // after a successful reject. The handler also clears the reason
        // buffer, which is why the three explicit resets are gone.
        onSuccess: () => handleRejectModalChange(false),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  /**
   * Reset the reason buffer when the modal closes via Cancel or
   * ESC/overlay-click. Without this, re-opening the modal on a
   * later candidate would carry the prior reason text — confusing
   * and a minor data-leak risk if the user assumed it was empty.
   */
  function handleRejectModalChange(open: boolean) {
    // OKS-998 — the internal state is kept in sync even while controlled, so
    // a parent that stops passing `rejectOpen` mid-life does not snap the
    // modal back to a stale value.
    setInternalRejectOpen(open)
    onRejectOpenChange?.(open)
    // Clear the whole buffer on close, so a later candidate cannot inherit a
    // previous one's code or note — the code matters more than the note here,
    // since a stale code is invisible in a collapsed select.
    if (!open) {
      setRejectReason("")
      setRejectReasonCode("")
      setRejectNoteMissing(false)
    }
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {/*
        OKS-465 — Remove from pipeline. Leftmost destructive affordance,
        SEPARATE from the OKS-19 transition `actions` surface (remove is
        not a status transition — it rides on `removal_actions.remove_url`).
        Rendered only when the viewer may remove (URL non-null).
      */}
      {removeUrl && (
        <Button
          type="button"
          variant="destructive"
          data-testid="candidate-detail-remove"
          aria-label={
            handle
              ? t("candidates.detail.actions.remove_aria", { handle })
              : undefined
          }
          disabled={submitting || !online}
          aria-disabled={submitting || !online}
          onClick={() => setRemoveDialogOpen(true)}
        >
          <UserMinus className="size-4" aria-hidden="true" />
          {t("candidates.detail.actions.remove")}
        </Button>
      )}
      {visibleButtons.map((def) => {
        const Icon = def.icon
        const isDisabled = submitting || !online
        return (
          <Button
            key={def.urlKey}
            type="button"
            variant={def.variant}
            data-testid={def.testId}
            disabled={isDisabled}
            aria-disabled={isDisabled}
            onClick={() => handleClick(def)}
          >
            <Icon className="size-4" aria-hidden="true" />
            {t(def.labelKey)}
          </Button>
        )
      })}
      {/*
        OKS-465 — Remove-from-pipeline destructive confirmation. shadcn
        AlertDialog (no reason field). Confirm fires a programmatic POST
        to `removal_actions.remove_url`; the dialog stays open on failure
        so the admin can retry (UX Spec §3 Error). Focus is trapped +
        restored to the trigger by AlertDialog primitives.
      */}
      <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <AlertDialogContent data-testid="candidate-detail-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("candidates.detail.actions.remove_dialog_title", {
                handle: handle ?? "",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("candidates.detail.actions.remove_dialog_description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>
              {t("candidates.detail.actions.remove_dialog_cancel")}
            </AlertDialogCancel>
            {/* OKS-1023 — plain `Button`, not `AlertDialogAction` (Base UI
                `Dialog.Close`, which would close the dialog on click). The
                dialog stays mounted while the POST is in flight and through a
                failure; `submitRemove` closes it on success. */}
            <Button
              type="button"
              data-testid="candidate-detail-remove-confirm"
              disabled={submitting || !online}
              className="bg-destructive hover:bg-destructive/90 text-white"
              onClick={submitRemove}
            >
              {t("candidates.detail.actions.remove_dialog_confirm")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/*
        OKS-18 reject confirmation modal — preserved inline. F10 will
        extend with the optional reason textarea per UX Spec §Reject
        modal extension. For F7 the modal stays exactly as OKS-18
        shipped (focus trap, ESC, focus restored), with the wire shape
        switched to the nested `{transition: {event: "reject"}}` form
        per BE Lead's locked contract.
      */}
      {/*
        F10 — extracted into a shared subtree so Drawer (<768px) and
        Dialog (≥768px) carry the same reason textarea + counter
        without duplication. The Cancel/Submit footers diverge by
        variant (Drawer's DrawerClose pattern, Dialog's manual onClick)
        so the footers stay branch-local.
      */}
      {isMobile ? (
        <Drawer open={rejectModalOpen} onOpenChange={handleRejectModalChange}>
          {/*
            OKS-998 — the same Base UI `finalFocus` getter as the Dialog
            branch below (OKS-1022: this Drawer is Base UI, no longer vaul).
          */}
          <DrawerContent finalFocus={rejectFinalFocus}>
            <DrawerHeader>
              <DrawerTitle>
                {t("candidates.detail.actions.reject_dialog_title")}
              </DrawerTitle>
              <DrawerDescription>
                {t("candidates.detail.actions.reject_dialog_description")}
              </DrawerDescription>
            </DrawerHeader>
            <div className="px-4 pb-2">
              <RejectionReasonPicker
                options={rejectOptions}
                value={rejectReasonCode}
                onValueChange={setRejectReasonCode}
                note={rejectReason}
                onNoteChange={setRejectReason}
                namePrefix="transition"
                idPrefix="candidate-detail-reject"
                noteTestId="candidate-detail-reject-reason"
                noteFooter={
                  <RejectReasonCounter
                    length={rejectReasonLength}
                    overSoft={rejectReasonOverSoft}
                    shouldAnnounce={rejectReasonShouldAnnounce}
                    noteRequired={isNoteRequired(rejectReasonCode)}
                  />
                }
                noteDescribedBy={REJECT_NOTE_HELP_ID}
                noteMissing={rejectNoteMissing}
                disabled={submitting}
              />
            </div>
            <DrawerFooter>
              <Button
                type="button"
                variant="destructive"
                onClick={submitReject}
                disabled={submitting || !online}
                data-testid="candidate-detail-reject-confirm"
              >
                {t("candidates.detail.actions.reject_dialog_confirm")}
              </Button>
              <DrawerClose
                disabled={submitting}
                render={<Button type="button" variant="ghost" />}
              >
                {t("candidates.detail.actions.reject_dialog_cancel")}
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={rejectModalOpen} onOpenChange={handleRejectModalChange}>
          {/*
            OKS-998 — Base UI's `finalFocus` getter, shared with the Drawer
            branch above; both bands must still be exercised.
          */}
          <DialogContent finalFocus={rejectFinalFocus}>
            <DialogHeader>
              <DialogTitle>
                {t("candidates.detail.actions.reject_dialog_title")}
              </DialogTitle>
              <DialogDescription>
                {t("candidates.detail.actions.reject_dialog_description")}
              </DialogDescription>
            </DialogHeader>
            <RejectionReasonPicker
              options={rejectOptions}
              value={rejectReasonCode}
              onValueChange={setRejectReasonCode}
              note={rejectReason}
              onNoteChange={setRejectReason}
              namePrefix="transition"
              idPrefix="candidate-detail-reject"
              noteTestId="candidate-detail-reject-reason"
              noteFooter={
                <RejectReasonCounter
                  length={rejectReasonLength}
                  overSoft={rejectReasonOverSoft}
                  shouldAnnounce={rejectReasonShouldAnnounce}
                  noteRequired={isNoteRequired(rejectReasonCode)}
                />
              }
              noteDescribedBy={REJECT_NOTE_HELP_ID}
              noteMissing={rejectNoteMissing}
              disabled={submitting}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => handleRejectModalChange(false)}
                disabled={submitting}
              >
                {t("candidates.detail.actions.reject_dialog_cancel")}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={submitReject}
                disabled={submitting || !online}
                data-testid="candidate-detail-reject-confirm"
              >
                {t("candidates.detail.actions.reject_dialog_confirm")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {/*
        OKS-950 — "Envoyer en validation" note dialog. Drawer <768px / Dialog
        ≥768px, matching every other modal on this bar.

        The note is OPTIONAL: confirming with an empty textarea posts exactly
        what the direct POST used to. It is worth a dialog anyway because this
        free text becomes the ENTIRE body of the brand's queue card — it is
        the only thing telling a brand manager why this creator was picked,
        and there is no other moment in the flow to capture it.
      */}
      {isMobile ? (
        <Drawer
          open={requestValidationOpen}
          onOpenChange={handleRequestValidationChange}
        >
          <DrawerContent data-testid="candidate-detail-request-validation-dialog">
            <DrawerHeader>
              <DrawerTitle>
                {t("candidates.detail.actions.request_validation_title", {
                  handle: handle ?? "",
                })}
              </DrawerTitle>
              <DrawerDescription>
                {t("candidates.detail.actions.request_validation_description")}
              </DrawerDescription>
            </DrawerHeader>
            <div className="px-4 pb-2">
              <ValidationNoteField
                value={validationNote}
                onChange={setValidationNote}
                disabled={submitting}
              />
            </div>
            <DrawerFooter>
              <Button
                type="button"
                onClick={submitRequestValidation}
                disabled={submitting || !online}
                data-testid="candidate-detail-request-validation-confirm"
              >
                {t("candidates.detail.actions.request_validation_confirm")}
              </Button>
              <DrawerClose
                disabled={submitting}
                render={<Button type="button" variant="ghost" />}
              >
                {t("candidates.detail.actions.request_validation_cancel")}
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog
          open={requestValidationOpen}
          onOpenChange={handleRequestValidationChange}
        >
          <DialogContent data-testid="candidate-detail-request-validation-dialog">
            <DialogHeader>
              <DialogTitle>
                {t("candidates.detail.actions.request_validation_title", {
                  handle: handle ?? "",
                })}
              </DialogTitle>
              <DialogDescription>
                {t("candidates.detail.actions.request_validation_description")}
              </DialogDescription>
            </DialogHeader>
            <ValidationNoteField
              value={validationNote}
              onChange={setValidationNote}
              disabled={submitting}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => handleRequestValidationChange(false)}
                disabled={submitting}
              >
                {t("candidates.detail.actions.request_validation_cancel")}
              </Button>
              <Button
                type="button"
                onClick={submitRequestValidation}
                disabled={submitting || !online}
                data-testid="candidate-detail-request-validation-confirm"
              >
                {t("candidates.detail.actions.request_validation_confirm")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

/**
 * OKS-950 — the optional note for the brand. Extracted so the Drawer and
 * Dialog branches share one field rather than duplicating it, matching
 * `RejectReasonField` below.
 */
interface ValidationNoteFieldProps {
  value: string
  onChange: (next: string) => void
  disabled: boolean
}

function ValidationNoteField({
  value,
  onChange,
  disabled,
}: ValidationNoteFieldProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="candidate-detail-validation-note">
        {t("candidates.detail.actions.request_validation_note_label")}
      </Label>
      <Textarea
        id="candidate-detail-validation-note"
        data-testid="candidate-detail-validation-note"
        name="transition[reason]"
        rows={4}
        maxLength={MAX_REJECT_REASON_LENGTH}
        placeholder={t(
          "candidates.detail.actions.request_validation_note_placeholder",
        )}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

/**
 * F10 — extracted reason textarea + counter for the OKS-18 reject
 * modal extension. UX Spec §Reject modal extension — OPTIONAL reason
 * applies to the rejection event row; visible to seated brand users
 * + assigned agency users on this brand (NOT cross-brand). Char
 * counter mirrors F5/F9 patterns: muted under 300, destructive at
 * ≥300; aria-live="polite" only ≥240 to avoid SR spam.
 *
 * Locked testid: `candidate-detail-reject-reason` (FE TRD §testid Map —
 * frozen at TRD publish; QA E2 binds against this).
 */
interface RejectReasonCounterProps {
  length: number
  overSoft: boolean
  shouldAnnounce: boolean
  /**
   * Whether the note is MANDATORY (`other` selected). Selects the helper
   * wording: the optional string ("Optional — visible to …") would contradict
   * the picker's « Note (obligatoire) » label, so the required string keeps
   * only the visibility disclosure. Derived by the caller from
   * `isNoteRequired`, the single rule the picker's label also reads.
   */
  noteRequired: boolean
}

/**
 * OKS-1012 — id of the helper `<p>` below, passed to the picker as
 * `noteDescribedBy` so the disclosure is the note's accessible description.
 * One constant for both branch call sites; only one branch is mounted at a
 * time, so the id is unique in the document.
 */
const REJECT_NOTE_HELP_ID = "candidate-detail-reject-reason-note-help"

/**
 * OKS-952 — what survives of `RejectReasonField`, injected through the
 * picker's `noteFooter` slot: the visibility helper and the soft-limit
 * counter.
 *
 * The helper renders in EVERY state (OKS-1012). Its text swaps with
 * `noteRequired` instead of disappearing: the note's visibility to the team
 * and the assigned agency is true whether or not the note is compelled, and it
 * matters most when it is. It is also the textarea's accessible description
 * (via `REJECT_NOTE_HELP_ID`); the counter is deliberately NOT part of it.
 *
 * The counter is cosmetic + a11y: the soft limit does NOT block submit (the
 * hard cap is the textarea's `maxLength`), and `aria-live` only engages near
 * the limit so a screen reader is not spammed on every keystroke.
 */
function RejectReasonCounter({
  length,
  overSoft,
  shouldAnnounce,
  noteRequired,
}: RejectReasonCounterProps) {
  const { t } = useTranslation()
  return (
    <div className="flex items-start justify-between gap-3">
      <p
        id={REJECT_NOTE_HELP_ID}
        data-testid="candidate-detail-reject-reason-helper"
        className="text-muted-foreground text-xs"
      >
        {noteRequired
          ? t("candidates.detail.actions.reject_reason_helper_required")
          : t("candidates.detail.actions.reject_reason_helper")}
      </p>
      <p
        data-testid="candidate-detail-reject-reason-counter"
        className={cn(
          "shrink-0 text-xs tabular-nums",
          overSoft ? "text-destructive font-medium" : "text-muted-foreground",
        )}
        aria-live={shouldAnnounce ? "polite" : "off"}
      >
        {t("candidates.detail.actions.reject_reason_counter", {
          count: length,
          limit: SOFT_REJECT_REASON_LIMIT,
        })}
      </p>
    </div>
  )
}
